import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { updateBugo } from '@shared/api/bugo/bugo.controller';
import { Bugo } from '@shared/api/bugo/bugo.interface';
import { updateCashAccount } from '@shared/api/cashAccount/cashAccount.controller';
import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { updateFevent } from '@shared/api/fevent/fevent.controller';
import { feventHookUrl } from '@shared/api/fevent/fevent.hook';
import { FeventOne, FeventUpdate } from '@shared/api/fevent/fevent.interface';
import { updateMember } from '@shared/api/member/member.controller';
import {
  Member,
  MemberUpdate,
  ShowCashAccountType,
} from '@shared/api/member/member.interface';
import { Button } from '@shared/components/Button';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

interface Props {
  fevent: FeventOne;
}

type MemberLocalState = {
  showCashAccountType: ShowCashAccountType;
  accountExpose: boolean | undefined;
  bugoId?: string;
  hasVerifiedAccount: boolean;
};

const FeventAccountSettingsForm: FC<Props> = ({ fevent }) => {
  const { bugoRole } = useAuth();
  const queryClient = useQueryClient();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  const memberList = fevent.memberOrderList as Member<string, Bugo, CashAccount>[];
  const feventCashAccounts = (fevent.cashAccounts ?? []) as CashAccount[];
  const verifiedAccounts = feventCashAccounts.filter((a) => a.isVerified);

  const [feventAccountExpose, setFeventAccountExpose] = useState(
    fevent.accountExpose ?? false,
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const buildInitialState = useCallback((): Record<string, MemberLocalState> => {
    const state: Record<string, MemberLocalState> = {};
    for (const member of memberList) {
      const bugo = member.bugo as Bugo | undefined;
      const cashAccount = member.cashAccount;
      const hasVerified =
        !!cashAccount &&
        typeof cashAccount === 'object' &&
        cashAccount.isVerified === true &&
        !!cashAccount.account;
      state[member._id] = {
        showCashAccountType: member.showCashAccountType ?? ShowCashAccountType.Self,
        accountExpose: bugo?.accountExpose,
        bugoId: bugo?._id,
        hasVerifiedAccount: hasVerified,
      };
    }
    return state;
  }, [memberList]);

  const [localState, setLocalState] =
    useState<Record<string, MemberLocalState>>(buildInitialState);

  const feventIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (feventIdRef.current === fevent._id) return;
    feventIdRef.current = fevent._id;
    setFeventAccountExpose(fevent.accountExpose ?? false);
    setLocalState(buildInitialState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fevent]);

  const hasChanges = useMemo(() => {
    if ((fevent.accountExpose ?? false) !== feventAccountExpose) return true;
    for (const member of memberList) {
      const local = localState[member._id];
      if (!local) continue;
      const originalType = member.showCashAccountType ?? ShowCashAccountType.Self;
      if (local.showCashAccountType !== originalType) return true;
      const bugo = member.bugo as Bugo | undefined;
      if (local.accountExpose !== bugo?.accountExpose) return true;
    }
    return false;
  }, [fevent.accountExpose, feventAccountExpose, localState, memberList]);

  const refreshFevent = useCallback(async () => {
    await queryClient.invalidateQueries(feventHookUrl(fevent._id, bugoRole()));
  }, [queryClient, fevent._id, bugoRole]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const promises: Promise<unknown>[] = [];

      if ((fevent.accountExpose ?? false) !== feventAccountExpose) {
        promises.push(
          updateFevent(
            { _id: fevent._id, accountExpose: feventAccountExpose } as FeventUpdate,
            bugoRole(),
          ),
        );
      }

      for (const member of memberList) {
        const local = localState[member._id];
        if (!local) continue;

        const originalType = member.showCashAccountType ?? ShowCashAccountType.Self;
        if (local.showCashAccountType !== originalType) {
          const update: MemberUpdate = {
            _id: member._id,
            showCashAccountType: local.showCashAccountType,
          };
          promises.push(updateMember(update, bugoRole()));
        }

        const bugo = member.bugo as Bugo | undefined;
        if (local.accountExpose !== bugo?.accountExpose && local.bugoId) {
          promises.push(
            updateBugo(
              { _id: local.bugoId, accountExpose: local.accountExpose ?? null } as any,
              bugoRole(),
            ),
          );
        }
      }

      await Promise.all(promises);
      await refreshFevent();
      notificateSuccess(notificationInstance, '계좌 설정 저장 완료');
    } catch (error) {
      console.error(error);
      notificateError(notificationInstance, '계좌 설정 저장 실패');
    } finally {
      setSaving(false);
    }
  }, [
    fevent,
    feventAccountExpose,
    localState,
    memberList,
    bugoRole,
    refreshFevent,
    notificationInstance,
  ]);

  const findOwnerMember = (accountId: string) =>
    memberList.find(
      (m) =>
        m.cashAccount &&
        typeof m.cashAccount === 'object' &&
        m.cashAccount._id === accountId,
    );

  const handleDeleteCashAccount = async (cashAccountId: string) => {
    const owner = findOwnerMember(cashAccountId);
    if (owner) {
      if (
        !window.confirm(
          `${owner.shortName}(${owner.relationType})님이 사용중인 계좌입니다. 정말 삭제하시겠습니까?`,
        )
      )
        return;
    } else {
      if (!window.confirm('이 계좌를 정말 삭제하시겠습니까?')) return;
    }
    setDeleting(true);
    try {
      await api_.delete(`/${bugoRole()}/cash-account/${cashAccountId}`);
      await refreshFevent();
      notificateSuccess(notificationInstance, '계좌 삭제 완료');
    } catch (error) {
      console.error(error);
      notificateError(notificationInstance, '계좌 삭제 실패');
    } finally {
      setDeleting(false);
    }
  };

  const getAdditionalAccountCount = (memberId: string) => {
    const ownAccountId = (() => {
      const m = memberList.find((m) => m._id === memberId);
      if (!m) return null;
      return m.cashAccount && typeof m.cashAccount === 'object'
        ? m.cashAccount._id
        : null;
    })();
    return verifiedAccounts.filter((a) => a._id !== ownAccountId).length;
  };

  return (
    <div className="text-sm">
      <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
        <p className="text-sm font-semibold text-blue-700">고급 기능 (선택사항)</p>
        <p className="mt-1 text-xs leading-relaxed text-blue-600">
          아래 설정은 필수가 아닙니다. 별도로 설정하지 않아도 부고 서비스 이용에 문제가
          없으며, 필요한 경우에만 선택적으로 활용하시면 됩니다.
        </p>
      </div>

      {/* 계좌 노출 설정 */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">계좌 바로 노출</p>
          <p className="text-xs font-normal text-gray-500">
            켜면 부고 페이지에서 계좌가 바로 펼쳐지고, 공유 메시지에 계좌 정보가
            포함됩니다.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setFeventAccountExpose((v) => !v)}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
            feventAccountExpose ? 'bg-blue-600' : 'bg-gray-200'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              feventAccountExpose ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* 인증된 계좌 목록 */}
      <div className="space-y-3 pt-8 font-bold">
        <h4>
          인증된 계좌 목록
          <span className="ml-1 text-xs font-normal text-gray-400">
            ({verifiedAccounts.length}개)
          </span>
        </h4>
        {verifiedAccounts.length === 0 ? (
          <p className="py-4 text-center text-xs font-normal text-gray-400">
            인증된 계좌가 없습니다.
          </p>
        ) : (
          <div className="space-y-2">
            {verifiedAccounts.map((account) => {
              const owner = findOwnerMember(account._id);
              return (
                <VerifiedAccountRow
                  key={account._id}
                  account={account}
                  owner={owner}
                  bugoRole={bugoRole()}
                  onDelete={() => handleDeleteCashAccount(account._id)}
                  onRefresh={refreshFevent}
                  disabled={deleting || saving}
                  notificationInstance={notificationInstance}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* 상주별 계좌 설정 */}
      {memberList.length > 0 && (
        <div className="space-y-3 pt-8 font-bold">
          <h4>상주별 계좌 설정</h4>
          <p className="text-xs font-normal text-gray-500">
            각 상주의 계좌 표시 방식과 노출 여부를 설정합니다.
          </p>

          <div className="space-y-4 pt-1">
            {memberList.map((member) => {
              const local = localState[member._id];
              if (!local) return null;
              const cashAccount =
                member.cashAccount && typeof member.cashAccount === 'object'
                  ? member.cashAccount
                  : null;
              const additionalCount = getAdditionalAccountCount(member._id);

              return (
                <div
                  key={member._id}
                  className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
                >
                  {/* 상주 헤더 */}
                  <p className="text-sm font-semibold">
                    {member.shortName}{' '}
                    <span className="font-normal text-gray-500">
                      ({member.relationType})
                    </span>
                  </p>

                  {/* 계좌 정보 (읽기 전용) */}
                  {cashAccount ? (
                    <div className="rounded-lg bg-gray-50 px-3 py-2.5 text-xs">
                      <div className="flex items-center justify-between">
                        <span>
                          <span className="font-medium">{cashAccount.bank}</span>
                          <span className="ml-1.5 text-gray-600">
                            {cashAccount.account}
                          </span>
                        </span>
                        {cashAccount.isVerified ? (
                          <span className="shrink-0 rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-bold text-green-700">
                            인증
                          </span>
                        ) : (
                          <span className="shrink-0 rounded-full bg-yellow-100 px-1.5 py-0.5 text-[10px] font-bold text-yellow-700">
                            미인증
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-gray-500">예금주: {cashAccount.name}</p>
                    </div>
                  ) : (
                    <div className="rounded-lg bg-gray-50 px-3 py-2.5 text-xs text-gray-400">
                      등록된 계좌 없음
                    </div>
                  )}

                  {/* 설정 영역 */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* 부의금 계좌 표시 */}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        부의금 계좌 표시
                      </label>
                      <select
                        value={local.showCashAccountType}
                        onChange={(e) =>
                          setLocalState((prev) => {
                            const existing = prev[member._id];
                            if (!existing) return prev;
                            return {
                              ...prev,
                              [member._id]: {
                                ...existing,
                                showCashAccountType: e.target
                                  .value as ShowCashAccountType,
                              },
                            };
                          })
                        }
                        disabled={saving}
                        className="input-box w-full px-2 py-2 text-sm font-medium theme-bg-1 theme-border-1"
                      >
                        <option value={ShowCashAccountType.Self}>본인만</option>
                        <option value={ShowCashAccountType.All}>
                          전체
                          {additionalCount > 0 ? ` (+${additionalCount}개)` : ''}
                        </option>
                      </select>
                    </div>

                    {/* 계좌 노출 */}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        계좌 노출
                      </label>
                      <select
                        value={
                          local.accountExpose === undefined
                            ? 'inherit'
                            : local.accountExpose
                            ? 'true'
                            : 'false'
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          const next = val === 'inherit' ? undefined : val === 'true';
                          setLocalState((prev) => {
                            const existing = prev[member._id];
                            if (!existing) return prev;
                            return {
                              ...prev,
                              [member._id]: { ...existing, accountExpose: next },
                            };
                          });
                        }}
                        disabled={saving || !local.bugoId || !local.hasVerifiedAccount}
                        className="input-box w-full px-2 py-2 text-sm font-medium theme-bg-1 theme-border-1 disabled:opacity-50"
                      >
                        <option value="inherit">설정 따라감</option>
                        <option value="true">켜기</option>
                        <option value="false">끄기</option>
                      </select>
                    </div>
                  </div>

                  {/* 전체 표시 시 안내 */}
                  {local.showCashAccountType === ShowCashAccountType.All &&
                    additionalCount > 0 && (
                      <p className="text-xs font-normal text-blue-500">
                        본인 계좌 외 {additionalCount}개 계좌가 추가로 표시됩니다.
                      </p>
                    )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {memberList.length === 0 && (
        <div className="pt-8 text-center text-sm text-gray-400">
          등록된 상주가 없습니다.
        </div>
      )}

      {/* 저장 버튼 */}
      <div className="space-y-2 pt-8">
        <Button
          className={`button-rectangle h-12 w-full text-white ${
            hasChanges ? 'theme-bg-main' : 'bg-gray-300'
          }`}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? '저장 중...' : hasChanges ? '저장' : '변경사항 없음'}
        </Button>

        {/* 부고 발송 버튼 */}
        <Link to={`/fevent/${fevent._id}/obituary-send`}>
          <Button className="button-rectangle mt-2 h-12 w-full text-white theme-bg-13">
            부고 발송
          </Button>
        </Link>
      </div>
    </div>
  );
};

interface VerifiedAccountRowProps {
  account: CashAccount;
  owner: { shortName: string; relationType: string } | undefined;
  bugoRole: LowerRole;
  onDelete: () => void;
  onRefresh: () => Promise<void>;
  disabled: boolean;
  notificationInstance: any;
}

const VerifiedAccountRow: FC<VerifiedAccountRowProps> = ({
  account,
  owner,
  bugoRole,
  onDelete,
  onRefresh,
  disabled,
  notificationInstance,
}) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(account.name);
  const [updatingName, setUpdatingName] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const handleSaveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    if (trimmed === account.name) {
      setEditing(false);
      return;
    }
    setUpdatingName(true);
    try {
      await updateCashAccount({ _id: account._id, name: trimmed }, bugoRole);
      await onRefresh();
      notificateSuccess(notificationInstance, '예금주명이 변경되었습니다.');
      setEditing(false);
    } catch {
      notificateError(notificationInstance, '예금주명 변경에 실패했습니다.');
    } finally {
      setUpdatingName(false);
    }
  };

  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="font-medium">{account.bank}</span>
          <span className="text-gray-600">{account.account}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
          {editing ? (
            <span className="flex items-center gap-1">
              <span className="shrink-0">예금주:</span>
              <input
                ref={inputRef}
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') {
                    setEditName(account.name);
                    setEditing(false);
                  }
                }}
                disabled={updatingName}
                className="w-20 rounded border border-blue-300 px-1.5 py-0.5 text-xs outline-none focus:ring-1 focus:ring-blue-400"
              />
              <button
                type="button"
                onClick={handleSaveName}
                disabled={updatingName || !editName.trim()}
                className="shrink-0 rounded bg-blue-500 px-1.5 py-0.5 text-[10px] font-bold text-white hover:bg-blue-600 disabled:opacity-50"
              >
                {updatingName ? '...' : '저장'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditName(account.name);
                  setEditing(false);
                }}
                disabled={updatingName}
                className="shrink-0 rounded px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                취소
              </button>
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <span>예금주: {account.name}</span>
              <button
                type="button"
                onClick={() => setEditing(true)}
                disabled={disabled}
                className="shrink-0 text-gray-400 hover:text-blue-500 disabled:opacity-50"
                title="예금주 수정"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="h-3 w-3"
                >
                  <path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L6.75 6.774a2.75 2.75 0 0 0-.596.892l-.848 2.047a.75.75 0 0 0 .98.98l2.047-.848a2.75 2.75 0 0 0 .892-.596l4.261-4.262a1.75 1.75 0 0 0 0-2.474Z" />
                  <path d="M4.75 3.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h6.5c.69 0 1.25-.56 1.25-1.25V9A.75.75 0 0 1 14 9v2.25A2.75 2.75 0 0 1 11.25 14h-6.5A2.75 2.75 0 0 1 2 11.25v-6.5A2.75 2.75 0 0 1 4.75 2H7a.75.75 0 0 1 0 1.5H4.75Z" />
                </svg>
              </button>
            </span>
          )}
          {!editing && owner && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-blue-500">
                {owner.shortName} ({owner.relationType})
              </span>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled || editing}
        className="ml-2 shrink-0 rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
      >
        삭제
      </button>
    </div>
  );
};

export default FeventAccountSettingsForm;
